/**
 * unifiedInboxAccess — Zielgruppen-Gate für die Unified Inbox "Belege &
 * Eingänge" (M-UX-5.2, `architecture-ux-restructure.md` §6.5/§8).
 *
 * Fix (2026-07-14): `UNIFIED_INBOX_ENABLED` war ein REIN globales Flag ohne
 * Beta-Pfad — in Prod dauerhaft AUS, wodurch die Unified Inbox NIEMANDEN
 * erreichte, obwohl ihre aggregierten Inhalte (Inbound via `canUseInbound`,
 * Scanner via `canUseReceiptScan`) längst eigene Beta-Gates haben. `canUseUnifiedInbox`
 * ersetzt das globale Flag als eigentliches Sichtbarkeits-Gate: sichtbar für
 * jeden, der MINDESTENS einen der aggregierten Kanäle nutzen darf, plus das
 * globale Flag als GA-Rollout-Override.
 *
 * Single source of truth für:
 *   - ob `/dashboard/eingaenge` für den aktuellen Nutzer erreichbar ist
 *     (Route-Registrierung bleibt grob/env-basiert in
 *     `routes/dashboardRoutePaths.ts`; die feine Per-Nutzer-Entscheidung trifft
 *     `routes/dashboardChildren.tsx` per Render über `canUseUnifiedInbox`),
 *   - ob die Alt-Routen (`/dashboard/posteingang`, `/dashboard/eingangsrechnungen`,
 *     `/dashboard/belege`) auf die Unified Inbox umleiten oder unverändert ihre
 *     bisherige Seite rendern (`routes/dashboardChildren.tsx`),
 *   - ob der neue `eingaenge`-Nav-Eintrag sichtbar ist UND ob die drei
 *     Alt-Einträge (`receivedInvoices`/`posteingang`/`receiptScan`) im
 *     Dokumente-Dropdown ausgeblendet werden (`config/navigation.ts`).
 *
 * @module config/unifiedInboxAccess
 */

import { canUseInbound } from './inboundAccess';
import { canUseReceiptScan } from './receiptScanAccess';
import type { Tier } from './entitlements';

/**
 * Allgemeines Feature-Flag für die Unified Inbox (GA-Rollout-Override, nicht
 * mehr der einzige Freischalt-Pfad — s. {@link canUseUnifiedInbox}). Fail-closed:
 * nur bei explizitem `'true'` aktiv. Modul-Konstante, einmalig beim Bundle-Load
 * ausgewertet (Muster `RECEIPT_SCAN_FLAG`/`INBOUND_ENABLED_FLAG`).
 */
export const UNIFIED_INBOX_ENABLED: boolean =
  process.env.REACT_APP_UNIFIED_INBOX_ENABLED === 'true';

/**
 * Entscheidet fail-closed, ob die Unified Inbox für einen Tenant-Scope
 * sichtbar/nutzbar ist. Sichtbar für jeden, der mindestens einen der
 * aggregierten Inhalte nutzen darf (Inbound ODER Belegscanner), zusätzlich
 * zum globalen GA-Flag als Rollout-Override.
 *
 * WICHTIG: `scopeUid` wird identisch an beide Einzel-Gates durchgereicht
 * (`canUseInbound` erwartet den Tenant-`effectiveScopeUid`, `canUseReceiptScan`
 * normalerweise die eigene `currentUser.uid` — für die aktuelle
 * Beta-Kohorte identisch, da Owner-Accounts `effectiveScopeUid === uid`
 * haben; kein Advisor-Fremd-Tenant-Fall in der Beta-Allowlist).
 *
 * @param scopeUid - `effectiveScopeUid` aus `useAuth()` (oder null/undefined,
 *                   solange noch nicht aufgelöst).
 * @param tier - effektiver Tier des Nutzers (`resolveTier(sellerData)`), 1:1 an
 *               `canUseReceiptScan` durchgereicht (Pricing-Repackaging PR-T4:
 *               der Scanner-OR-Zweig ist ab GA Ultra-gegatet — inert, solange die
 *               Flags live aus sind; nur die Beta-Allowlist greift).
 * @returns `true`, wenn `canUseInbound` ODER `canUseReceiptScan` greift ODER
 *          das globale Flag aktiv ist. Sonst `false`.
 */
export function canUseUnifiedInbox(scopeUid: string | null | undefined, tier: Tier): boolean {
  return canUseInbound(scopeUid) || canUseReceiptScan(scopeUid, tier) || UNIFIED_INBOX_ENABLED;
}
